import { request } from './client'

/* Reported faults and guest requests, as the server sends them.

   NOTE WHAT IS ABSENT from both: no `isBlocking` on an issue and no `isLate` stored on
   a request. `severity` is what the room cares about, and blocking-ness is derived from
   it in one place; lateness IS sent, but computed on read from the promise and the
   SERVER's clock, because a handset with a wrong device time would otherwise colour
   rows red at random. */

export type IssueSeverity = 'MINOR' | 'MAJOR' | 'BLOCKING'
export type IssueState = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'CANCELLED'

export interface IssueView {
  issueId: string
  roomId: string | null
  roomNumber: string | null
  categoryCode: string
  severity: IssueSeverity
  state: IssueState
  description: string | null
  reportedByName: string
  reportedByNameAr: string | null
  reportedAtUtc: string
  /** Whether THIS issue is what took the room out of order — see IssueService. */
  tookPlaceOutOfOrder: boolean
  closureReason: string | null
  /** How many photographs are attached. A COUNT, not the photographs — the board draws
   *  forty of these and fetching thumbnails for each would be hundreds of requests to
   *  answer "is there a picture?". Removed ones are not counted. */
  photoCount: number
}

export type RequestState =
  | 'OPEN' | 'ACKNOWLEDGED' | 'IN_PROGRESS' | 'FULFILLED' | 'DECLINED' | 'CANCELLED'

export type RequestChannel = 'FRONT_DESK' | 'SUPERVISOR' | 'GUEST_FORM'

export interface RequestView {
  requestId: string
  roomId: string
  roomNumber: string
  categoryCode: string
  state: RequestState
  channel: RequestChannel
  quantity: number | null
  notes: string | null
  raisedAtUtc: string
  promisedByUtc: string
  isLate: boolean
  closureReason: string | null
}

/**
 * A blocking fault is the only severity that takes a room off sale.
 *
 * The same rule as the C# `IssueLifecycle.TakesRoomOutOfOrder`, implemented here for
 * the same reason readiness is: the handset decides what to show while standing in a
 * corridor with no signal. It is one line and it is a liability, so it is written where
 * both screens can find it rather than in each of them.
 */
export function takesRoomOutOfOrder(severity: IssueSeverity): boolean {
  return severity === 'BLOCKING'
}

export function fetchIssues(
  propertyId: string, options: { signal?: AbortSignal } = {},
): Promise<IssueView[]> {
  return request<IssueView[]>(
    `/properties/${propertyId}/issues`,
    options.signal ? { signal: options.signal } : {})
}

export function raiseIssue(
  propertyId: string,
  body: {
    roomId?: string
    areaId?: string
    categoryCode: string
    severity: IssueSeverity
    description?: string
    /** DEVICE time when the person reported it — not when it reached us. */
    capturedAtUtc?: string
  },
): Promise<{ issueId: string }> {
  return request(`/properties/${propertyId}/issues`, { method: 'POST', body })
}

export function changeIssueState(
  issueId: string, body: { toState: IssueState; reason?: string },
): Promise<void> {
  return request<void>(`/issues/${issueId}/state`, { method: 'POST', body })
}

/* ── W-71 · ONE FAULT, IN FULL (Sprint 64) ─────────────────────────────────────

   A SECOND READ AND NOT A FATTER FIRST ONE. `IssueView` is what forty cards need; this is
   what one open card needs when somebody asks why the room is still off sale. Fetching it
   per card would cost the board a history query and three actor joins forty times over,
   every thirty seconds, to answer a question nobody has asked. */

export interface FaultEvent {
  serviceEventId: string
  /** The wire word, or `-` on the raising, which came from nothing. */
  fromState: string
  toState: string
  reason: string | null
  /** **NULL IS A REAL ANSWER**: the guest form is nobody. */
  actorName: string | null
  actorNameAr: string | null
  occurredAtUtc: string
}

export interface FaultDetailView {
  issueId: string
  propertyId: string
  roomId: string | null
  roomNumber: string | null
  floorCode: string | null
  areaId: string | null
  /** The room number or the area's name — the XOR resolved once, on the server. */
  place: string
  placeAr: string | null
  categoryCode: string
  categoryName: string
  categoryNameAr: string | null
  severity: IssueSeverity
  state: IssueState
  description: string | null
  reportedByName: string
  reportedByNameAr: string | null
  reportedAtUtc: string
  /** Device time. Differs from `reportedAtUtc` on a replayed handset report. */
  capturedAtUtc: string | null
  acknowledgedAtUtc: string | null
  acknowledgedByName: string | null
  acknowledgedByNameAr: string | null
  closedAtUtc: string | null
  closedByName: string | null
  closedByNameAr: string | null
  closureReason: string | null
  /** Whether THIS fault is what shut the place. History. */
  tookPlaceOutOfOrder: boolean
  /** Whether the place is shut NOW. The present. Deliberately not the same field. */
  placeIsOutOfOrder: boolean
  otherOpenFaultsHere: number
  /** Of those, the ones that will still hold the place after this one closes. */
  otherBlockingFaultsHere: number
  photoCount: number
  /** Reported to closed, or reported to now. Derived on read, never stored. */
  minutesOpen: number
  events: FaultEvent[]
}

export function fetchFaultDetail(
  issueId: string, options: { signal?: AbortSignal } = {},
): Promise<FaultDetailView> {
  return request<FaultDetailView>(
    `/issues/${issueId}`,
    options.signal ? { signal: options.signal } : {})
}

export function fetchRequests(
  propertyId: string, options: { signal?: AbortSignal } = {},
): Promise<RequestView[]> {
  return request<RequestView[]>(
    `/properties/${propertyId}/requests`,
    options.signal ? { signal: options.signal } : {})
}

export function raiseRequest(
  propertyId: string,
  body: {
    roomId: string
    categoryCode: string
    channel: RequestChannel
    quantity?: number
    notes?: string
    promiseMinutes?: number
  },
): Promise<{ requestId: string }> {
  return request(`/properties/${propertyId}/requests`, { method: 'POST', body })
}

export function changeRequestState(
  requestId: string, body: { toState: RequestState; reason?: string },
): Promise<void> {
  return request<void>(`/requests/${requestId}/state`, { method: 'POST', body })
}

/* ── M-06 / M-07: GUEST REQUESTS AS ONE ATTENDANT SEES THEM (Sprint 42) ─────────

   TWO LISTS AND NOT ONE FILTERED LIST WITH A FLAG. Pack 6: *"'Waiting for anyone on this
   floor' is a separate section, above the attendant's own work. An unaccepted request has
   no owner, so nobody feels late for it — putting it at the top with a 'take it' badge is
   the whole mechanism."* A flag would let a screen draw them together, and the moment they
   are drawn together the unowned ones are buried under somebody's own four. */

/** What delivering it will take off a shelf. Null when nothing will move. */
export interface HandsetFulfilment {
  stockItemCode: string
  itemName: string
  itemNameAr: string | null
  unit: string
  quantity: number
  /** The floor's pantry today; the pair's trolley once M-09 exists. */
  fromLocationName: string
  fromLocationNameAr: string | null
}

export interface HandsetRequest {
  requestId: string
  roomId: string
  roomNumber: string
  floorCode: string
  categoryCode: string
  categoryName: string
  categoryNameAr: string | null
  state: RequestState
  channel: string
  quantity: number | null
  /** THE GUEST'S OWN WORDS, never translated. */
  notes: string | null
  raisedAtUtc: string
  promisedByUtc: string
  /**
   * NEGATIVE IS TIME LEFT, POSITIVE IS TIME PAST. One number rather than a boolean and a
   * duration — "due in 18 minutes" and "9 minutes past the promised time" are the same
   * fact with a sign on it, and two fields invite a row that says both at once.
   */
  minutesFromPromise: number
  fulfilment: HandsetFulfilment | null
}

export interface HandsetRequestsView {
  localDate: string
  generatedAtUtc: string
  /** Nobody has taken these, and they are on a floor he holds. */
  waiting: HandsetRequest[]
  /** His own, wherever they are. */
  mine: HandsetRequest[]
  /**
   * **W-63's FALLBACK — NOBODY IS WORKING THAT FLOOR TODAY.** Sprint 74.
   *
   * The client's answer was *auto-route by floor*, falling back to the shared board. The
   * routing has worked since Sprint 42; **the fallback did not exist**, so a request in a
   * room on an unstaffed floor was on nobody's handset at all, open, with its promise
   * running. This hotel has that shape: pilgrim accommodation fills and empties with the
   * season, and the guest form is open to a room whether or not anybody is rostered above
   * it.
   *
   * **KEPT APART FROM `waiting` ON PURPOSE.** "Nobody has taken this on your floor" is his
   * round; "nobody is working this floor at all" is a staffing gap he is being asked to
   * cover, possibly two buildings away. An attendant who cannot tell them apart walks it
   * believing it was always his.
   */
  unheld: HandsetRequest[]
}

/**
 * **THE ACTOR IS THE SESSION AND NOT A PARAMETER.** There is no legitimate reason for one
 * attendant to ask what is waiting on another's floor, and a parameter invites exactly that.
 */
export const fetchMyRequests = (options: { signal?: AbortSignal } = {}) =>
  request<HandsetRequestsView>(
    '/me/requests',
    options.signal ? { signal: options.signal } : {})
